/**
 * InvoiceForm — create a new outbound invoice (POST /v1/finance/issued-invoices).
 * Pre-fills the hybrid invoice number (GET .../next-number) and seeds currency /
 * due date / auto-send from tenant settings. The live totals preview MIRRORS the
 * server math (round per line, then sum); the backend stays authoritative on save.
 * Line items carry the wire's snake_case keys (unit_price/tax_rate); tax is entered
 * as a percentage and converted to the fraction the API expects.
 */
import { useState, useEffect } from 'react'
import { Plus, Trash2, ArrowLeft } from 'lucide-react'
import { SectionTitle, Toggle, LoadingState } from '../../DashboardShared'
import { CurrencySelect } from '../../CurrencySelect'
import { fetchNextInvoiceNumber, createIssuedInvoice, fetchSettings } from '../../api'
import type { IssuedInvoice, IssuedInvoiceLineItem } from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { money } from '../../../../lib/money'
import { toast } from 'sonner'

const inputCls = 'w-full px-3 py-2 border border-edge-control rounded-control text-13 text-ink-primary placeholder-ink-section-label transition-all'
// Line-item inputs sit in a flex row, so they must NOT carry w-full (it fights the
// per-field widths and collapses the description). Width comes from flex-1 / w-* below.
const lineInputCls = 'px-3 py-2 border border-edge-control rounded-control text-13 text-ink-primary placeholder-ink-section-label transition-all'
const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

function todayISO(): string { return new Date().toISOString().slice(0, 10) }
function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}
function round2(x: number): number { return Math.round(x * 100) / 100 }

interface FormLine { description: string; quantity: number; unitPrice: number; taxPct: number }
const emptyLine = (): FormLine => ({ description: '', quantity: 1, unitPrice: 0, taxPct: 0 })

export default function InvoiceForm({ onSaved, onCancel }: { onSaved: (inv: IssuedInvoice) => void; onCancel: () => void }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [clientName, setClientName] = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [clientAddress, setClientAddress] = useState('')
  const [issueDate, setIssueDate] = useState(todayISO())
  const [dueDate, setDueDate] = useState('')
  const [currency, setCurrency] = useState('GBP')
  const [notes, setNotes] = useState('')
  const [autoSend, setAutoSend] = useState(false)
  const [lines, setLines] = useState<FormLine[]>([emptyLine()])

  useEffect(() => {
    let cancelled = false
    Promise.allSettled([fetchNextInvoiceNumber(), fetchSettings()]).then(([numRes, setRes]) => {
      if (cancelled) return
      let terms = 30
      if (setRes.status === 'fulfilled') {
        const s = setRes.value
        if (s.defaultCurrency) setCurrency(s.defaultCurrency)
        if (typeof s.defaultPaymentTermsDays === 'number' && s.defaultPaymentTermsDays > 0) terms = s.defaultPaymentTermsDays
        setAutoSend(!!s.autoSend)
      }
      setDueDate(addDaysISO(todayISO(), terms))
      if (numRes.status === 'fulfilled') setInvoiceNumber(numRes.value.invoiceNumber)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  if (loading) return <LoadingState />

  const setLine = (i: number, patch: Partial<FormLine>) => setLines(ls => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)))
  const addLine = () => setLines(ls => [...ls, emptyLine()])
  const removeLine = (i: number) => setLines(ls => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls))

  let subtotal = 0, taxTotal = 0
  for (const l of lines) {
    const lineTotal = round2((l.quantity || 0) * (l.unitPrice || 0))
    subtotal += lineTotal
    taxTotal += lineTotal * ((l.taxPct || 0) / 100)
  }
  subtotal = round2(subtotal)
  taxTotal = round2(taxTotal)
  const total = round2(subtotal + taxTotal)

  async function save(status: 'draft' | 'issued') {
    if (!clientName.trim()) { toast.error('Client name is required'); return }
    setSaving(true)
    try {
      const lineItems: IssuedInvoiceLineItem[] = lines
        .filter(l => l.description.trim() !== '' || l.unitPrice !== 0 || l.quantity !== 0)
        .map(l => ({
          description: l.description,
          quantity: l.quantity || 0,
          unit_price: l.unitPrice || 0,
          tax_rate: (l.taxPct || 0) / 100,
        }))
      const inv = await createIssuedInvoice({
        invoiceNumber, clientName, clientEmail, clientAddress,
        issueDate, dueDate, currency, lineItems, notes, autoSend, status,
      })
      toast.success(status === 'issued' ? 'Invoice created and issued' : 'Draft invoice created')
      onSaved(inv)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create invoice'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1000 }}>
      <button onClick={onCancel} className="flex items-center gap-1 text-12 text-ink-muted hover:text-ink-primary transition-colors w-fit">
        <ArrowLeft size={13} /> Back to invoices
      </button>
      <SectionTitle title="New invoice" subtitle="Issue an invoice to a client" />

      <div className="k-card">
        <div className="p-5 flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Client name *</label>
              <input className={inputCls} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="e.g. Acme Ltd" />
            </div>
            <div>
              <label className={labelCls}>Invoice number</label>
              <input className={inputCls} value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="INV-0001" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Client email</label>
              <input type="email" className={inputCls} value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="billing@acme.com" />
            </div>
            <div>
              <label className={labelCls}>Currency</label>
              {/* The increment-3 picker retires the free-text input: options
                  are the server's capturable set, so a doomed code can no
                  longer be typed. Still seeded from the tenant default. */}
              <CurrencySelect className={inputCls} value={currency} onChange={setCurrency} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Client address</label>
            <textarea className={inputCls + ' resize-none'} rows={2} value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder="Street, city, postcode" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Issue date</label>
              <input type="date" className={inputCls} value={issueDate} onChange={e => setIssueDate(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Due date</label>
              <input type="date" className={inputCls} value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="k-card">
        <div className="p-5 flex flex-col gap-3">
          <div className="text-13 font-semibold text-ink-primary">Line items</div>
          <div className="finance-thead flex items-center gap-2">
            <span className="flex-1">Description</span>
            <span className="w-20 text-right">Qty</span>
            <span className="w-28 text-right">Unit price</span>
            <span className="w-20 text-right">VAT %</span>
            <span className="w-28 text-right">Line total</span>
            <span className="w-8" />
          </div>
          {lines.map((l, i) => {
            const lineTotal = round2((l.quantity || 0) * (l.unitPrice || 0))
            return (
              <div key={i} className="flex items-center gap-2">
                <input className={lineInputCls + ' flex-1 min-w-0'} value={l.description} onChange={e => setLine(i, { description: e.target.value })} placeholder="Description" />
                <input type="number" className={lineInputCls + ' w-20 text-right'} value={l.quantity} min={0} step="any" onChange={e => setLine(i, { quantity: Number(e.target.value) })} />
                <input type="number" className={lineInputCls + ' w-28 text-right'} value={l.unitPrice} min={0} step="any" onChange={e => setLine(i, { unitPrice: Number(e.target.value) })} />
                <input type="number" className={lineInputCls + ' w-20 text-right'} value={l.taxPct} min={0} step="any" onChange={e => setLine(i, { taxPct: Number(e.target.value) })} />
                <span className="w-28 text-right text-13 font-medium text-ink-primary tabular-nums">{money(lineTotal, currency)}</span>
                <button onClick={() => removeLine(i)} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:text-danger hover:bg-danger-bg transition-colors" aria-label="Remove line">
                  <Trash2 size={14} />
                </button>
              </div>
            )
          })}
          <button onClick={addLine} className="flex items-center gap-1 text-12 font-medium text-accent hover:opacity-80 transition-opacity w-fit mt-1">
            <Plus size={13} /> Add line
          </button>
          <div className="mt-3 pt-3 border-t border-edge-row flex flex-col gap-1 items-end">
            <div className="flex gap-8 text-13"><span className="text-ink-muted">Subtotal</span><span className="w-28 text-right tabular-nums text-ink-primary">{money(subtotal, currency)}</span></div>
            <div className="flex gap-8 text-13"><span className="text-ink-muted">VAT</span><span className="w-28 text-right tabular-nums text-ink-primary">{money(taxTotal, currency)}</span></div>
            <div className="flex gap-8 text-14 font-semibold"><span className="text-ink-primary">Total</span><span className="w-28 text-right tabular-nums text-ink-primary">{money(total, currency)}</span></div>
          </div>
        </div>
      </div>

      <div className="k-card">
        <div className="p-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Notes</label>
            <textarea className={inputCls + ' resize-none'} rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Payment terms, thank-you note, PO reference…" />
          </div>
          <div className="flex items-center justify-between border-t border-edge-row pt-4">
            <div>
              <div className="text-13 font-medium text-ink-primary">Auto-send when due</div>
              <div className="text-12 text-ink-muted">Email this invoice to the client automatically on its due date.</div>
            </div>
            <Toggle checked={autoSend} onChange={setAutoSend} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button className="k-btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="k-btn-secondary" onClick={() => save('draft')}>{saving ? 'Saving…' : 'Save as draft'}</button>
        <button className="k-btn k-btn--lg" onClick={() => save('issued')}>{saving ? 'Saving…' : 'Create & issue'}</button>
      </div>
    </div>
  )
}
